/**************************************************************************************************/
/** @import Node Packages                                                                        **/
/**************************************************************************************************/
import { defineComponent } from 'vue'

/**************************************************************************************************/
/** @import Config                                                                               **/
/**************************************************************************************************/
import Config from '@/config'

/**************************************************************************************************/
/** @import Constants                                                                            **/
/**************************************************************************************************/
import { Server } from '@/Constants/Server'
import { VueRouter } from '@/Constants/VueRouter'

/**************************************************************************************************/
/** @import Helpers/Utils                                                                        **/
/**************************************************************************************************/
import { TypesHelper } from '@/Helpers/Utils/TypesHelper'

/**************************************************************************************************/
/** @import Mixins                                                                               **/
/**************************************************************************************************/
import MixinBase from '@/Mixins/MixinBase'
import MixinComponent from '@/Mixins/MixinComponent'
import MixinFetch from '@/Mixins/MixinFetch'

/**************************************************************************************************/
/** @internal Const @name PasswordResetView                                                      **/
/**************************************************************************************************/
const PasswordResetView = defineComponent(
{
	/*************************************************/
	/** @Properties                                 **/
	/*************************************************/
	name: VueRouter.Views.Global.PASSWORD_RESET.NAME, mixins: [MixinBase, MixinComponent, MixinFetch],

	/*************************************************/
	/** @Data                                       **/
	/*************************************************/
	data()
	{
		return {
			states:
			{
				_id: undefined as string | undefined,
				action: 'SEND_MAIL',
				email: '',
				code: '',
				isFetching: false,
				password: ''
			}
		}
	},

	/*************************************************/
	/** @Hooks                                      **/
	/*************************************************/
	async created() : Promise<void>
	{
		// Ingresó por medio del Correo enviado.
		const { action, email } = this.$route.query
		if (action === 'ENTER_CODE') this.Set<PasswordResetViewRef['states']>({ action, email: email as string })
	},

	/*************************************************/
	/** @Computed                                   **/
	/*************************************************/
	computed:
	{
		IsActionEnterCode() : boolean
		{
			return this.states.action === 'ENTER_CODE'
		},

		IsActionMailSent() : boolean
		{
			return this.states.action === 'MAIL_SENT'
		},

		IsActionSendMail() : boolean
		{
			return this.states.action === 'SEND_MAIL'
		},

		IsActionSendPassword() : boolean
		{
			return this.states.action === 'SEND_NEW_PASSWORD'
		},

		IsValidEmail() : boolean
		{
			return TypesHelper.isStringValidEmailFormat(this.states.email)
		}
	},

	/*************************************************/
	/** @Methods                                    **/
	/*************************************************/
	methods:
	{
		GetBasePath() : string
		{
			const { protocol, hostname } = Config.axios
			return `${ protocol }://${ hostname }`
		},

		GetDescription() : string | undefined
		{
			switch (this.states.action)
			{
				case 'ENTER_CODE'       : return `Hemos enviado una codigo de verificacion a ${ this.states.email } Ingresando la codigo de verificacion podrá restablecer su contraseña.`
				case 'MAIL_SENT'        : return 'En caso de ser un E-mail autorizado, se enviará una clave de acceso para que pueda restablecer su contraseña.'
				case 'PROCESS_COMPLETED': return 'Ahora puede regresar al Login e Iniciar Sesión con su nueva Contraseña.'
				case 'SEND_MAIL'        : return 'Ingrese el correo electrónico asociado a su cuenta, esto le permitirá establecer una nueva contraseña.'
				case 'SEND_NEW_PASSWORD': return 'Ingrese una nueva Contraseña, la cual será actualizada en la cuenta asociada al Correo que fue especificado anteriormente.'
			}
		},

		GetPlaceHolder() : string | undefined
		{
			switch (this.states.action)
			{
				case 'ENTER_CODE'       : return 'Ingrese el Código de Verificación'
				case 'SEND_MAIL'        : return 'Ingrese su Correo Electrónico'
				case 'SEND_NEW_PASSWORD': return 'Ingresar Contraseña'
			}
		},

		GetSubmitButtonText() : string | undefined
		{
			// Mensaje cuando una Petición se encuentra en curso.
			if (this.states.isFetching) return 'Por favor, espere un momento.'

			switch (this.states.action)
			{
				case 'ENTER_CODE'       : return 'Verificar Código'
				case 'SEND_MAIL'        : return 'Siguiente'
				case 'SEND_NEW_PASSWORD': return 'Actualizar Contraseña'
			}
		},

		GetTitle() : string | undefined
		{
			switch (this.states.action)
			{
				case 'ENTER_CODE'       : return 'Código de Verificacción'
				case 'MAIL_SENT'        : return 'Mensaje Enviado!'
				case 'PROCESS_COMPLETED': return 'Proceso Finalizado'
				case 'SEND_MAIL'        : return 'Restablecer Contraseña'
				case 'SEND_NEW_PASSWORD': return 'Ingresar Nueva Contraseña'
			}
		},

		OnBackToLogin() : void
		{
			this.$router.push({ name: VueRouter.Views.Global.LOGIN.NAME })
		},

		OnEmailInput() : void
		{
			this.states.email = this.states.email.toLowerCase()
		},

		async OnSendCode() : Promise<void>
		{
			const { code, email } = this.states
			if (code && code.trim() !== '')
			{
				// Objeto con las Propiedades requeridas por la Petición.
				const body = { email, code }

				// Realizar la Petición al servidor.
				this.Set<PasswordResetViewRef['states']>({ isFetching: true })
				const response = await this.Fetch(Server.Fetching.Method.POST, Server.Routes.Emails.VerifyCode, body)

				// Si se obtiene una respuesta satisfactoria, continuar con el proceso.
				if (response?.status === Server.Response.StatusCodes.SUCCESS && response.data.body)
				{
					this.ShowToast('Verificación Exitosa', 'A continuación, especifique una nueva Contraseña', 'success')
					this.Set<PasswordResetViewRef['states']>({ _id: (response.data.body as any)._id, action: 'SEND_NEW_PASSWORD', code: '', email: '', isFetching: false })
					return
				}

				// Especificar que el Código de Verificación sea valido.
				this.ShowToast('INVALID_CODE')
				this.Set<PasswordResetViewRef['states']>({ isFetching: false })
				return
			}

			// Mostrar Mensaje Toast.
			this.ShowToast('EMPTY_CODE')
		},

		async OnSendEmail() : Promise<void>
		{
			const { email } = this.states
			if (email && email.trim() !== '')
			{
				if (this.IsValidEmail)
				{
					// Objeto con las Propiedades requeridas por la Petición.
					const body =
					{
						email,
						link: `${ this.GetBasePath() }/#${ VueRouter.Views.Global.PASSWORD_RESET.PATH }?action=ENTER_CODE&email=${ email }`
					}

					// Realizar la Petición al servidor.
					this.Set<PasswordResetViewRef['states']>({ isFetching: true })
					const response = await this.Fetch(Server.Fetching.Method.POST, Server.Routes.Emails.RequestPassword, body)

					// Si se obtiene una respuesta satisfactoria, continuar con el proceso.
					if (response?.status === Server.Response.StatusCodes.SUCCESS)
					{
						this.ShowToast('Correo Enviado', 'Se ha enviado un correo con las instrucciones para reiniciar tu contraseña!', 'success')
						this.Set<PasswordResetViewRef['states']>({ action: 'MAIL_SENT', email: '', isFetching: false })
						return
					}
				}

				// Especificar que el formato del Correo no es valido.
				this.ShowToast('INVALID_EMAIL_FORMAT')
				this.Set<PasswordResetViewRef['states']>({ isFetching: false })
				return
			}

			// Mostrar Mensaje Toast.
			this.ShowToast('SEND_MAIL')
		},

		async OnSendNewPassword() : Promise<void>
		{
			const { _id, email, password } = this.states
			if ((email && email.trim() !== '') && (password && password.trim() !== '') && (email === password))
			{
				// Objeto con las Propiedades requeridas por la Petición.
				const body = { _id, password, email: this.$route.query.email }

				// Realizar la Petición al servidor.
				this.Set<PasswordResetViewRef['states']>({ isFetching: true })
				const response = await this.Fetch(Server.Fetching.Method.PATCH, Server.Routes.Users.UpdateUserPassword, body)

				// Si se obtiene una respuesta satisfactoria, continuar con el proceso.
				if (response?.status === Server.Response.StatusCodes.SUCCESS)
				{
					this.ShowToast('Contraseña Restablecida!', 'Su Contraseña fue actualizada correctamente!', 'success')
					this.Set<PasswordResetViewRef['states']>({ action: 'PROCESS_COMPLETED', email: '', isFetching: false })
					return
				}
			}

			// Mostrar Mensaje Toast.
			this.ShowToast('VERIFY_PASSWORD')
		},

		OnServerCaughtFetchException(path: string, resolution: Server.Fetching.Resolutions, status: number, error: any) : void
		{
			if (path === Server.Routes.Emails.VerifyCode)
			{
				// Especificar que el Código de Verificación sea valido.
				this.ShowToast('INVALID_CODE')
				this.Set<PasswordResetViewRef['states']>({ isFetching: false })
			}
		},

		OnSubmitClick() : Promise<void> | undefined
		{
			switch (this.states.action)
			{
				case 'ENTER_CODE'       : return this.OnSendCode()
				case 'SEND_MAIL'        : return this.OnSendEmail()
				case 'SEND_NEW_PASSWORD': return this.OnSendNewPassword()
			}
		},

		ShowToast(key: string) : void
		{
			switch (key)
			{
				case 'SEND_MAIL':
					return this.ShowToast('Campo Requerido', 'Debe rellenar el campo con un Correo Electronico que sea valido!', 'warning')
				case 'EMPTY_CODE':
					return this.ShowToast('Campo Requerido', 'Debe especificar un Código de Verificación', 'warning')
				case 'INVALID_EMAIL_FORMAT':
					return this.ShowToast('Correo Electrónico No Válido', 'Debe ingresar un Correo con un formato válido!', 'warning')
				case 'INVALID_CODE':
					return this.ShowToast('Código No Valido', 'Compruebe que el Código de Verificación es el mismo que fue enviado a su correo, además tenga presente que el Código es valido unicamente por 30 minutos!', 'warning')
				case 'VERIFY_PASSWORD':
					return this.ShowToast('Verifique Campos', 'Verifique que ambos campos hayan sido rellenados y que ambas Contraseñas sean exactamente iguales!', 'warning')
			}
		}
	}
})

/**************************************************************************************************/
/** @export Component @name PasswordResetView                                                    **/
/**************************************************************************************************/
export default PasswordResetView

/**************************************************************************************************/
/** @export Type @name PasswordResetViewRef                                                      **/
/**************************************************************************************************/
export type PasswordResetViewRef = InstanceType<typeof PasswordResetView>